import { useDeferredValue, useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import { createFilterSearchService } from "../services/filter-search-service/factory";
import type {
    FilterSearchConfigMap,
    IFilterSearchService,
} from "../services/filter-search-service/types";
import type { SearchFilterInstance } from "../types/searchFilterInstance";
import advancedFilteringI18nKeys from "../utils/i18n";

/**
 * Local-state equivalent of `useFilterListSearch`: same pure `filter-search-service`
 * underneath, but the search query is sourced from caller-supplied state instead of
 * the singleton Redux slice.
 */
type SearchResults<TFilter extends SearchFilterInstance, TKey extends string> = {
    matchedFilters: readonly TFilter[];
    autoExpandFilterKey: TKey | null;
};

type Params<TFilter extends SearchFilterInstance, TKey extends string> = {
    filtersConfig: readonly TFilter[];
    searchConfigMaps?: readonly FilterSearchConfigMap<TKey>[];
    searchQuery: string;
};

type Result<TFilter extends SearchFilterInstance, TKey extends string> = {
    searchResults: SearchResults<TFilter, TKey> | null;
    isSearchActive: boolean;
};

export const useLocalFilterListSearch = <
    TFilter extends SearchFilterInstance = SearchFilterInstance,
    TKey extends string = string,
>({
    filtersConfig,
    searchConfigMaps,
    searchQuery,
}: Params<TFilter, TKey>): Result<TFilter, TKey> => {
    const { translate, getLocale } = useTranslate();
    const locale = getLocale();

    const deferredSearchQuery = useDeferredValue(searchQuery);

    const isSearchActive = !!searchConfigMaps && searchQuery.trim().length > 0 && deferredSearchQuery.trim().length > 0;

    const service = useMemo<IFilterSearchService<TKey> | null>(
        () =>
            searchConfigMaps
                ? createFilterSearchService({
                      searchConfigMaps,
                      getCategoryI18nKey: advancedFilteringI18nKeys.category,
                  })
                : null,
        [searchConfigMaps],
    );

    const searchResults = useMemo(() => {
        if (!service || !deferredSearchQuery.trim()) {
            return null;
        }

        const { matchedIds, autoExpandFilterKey } = service.search({
            filters: filtersConfig,
            query: deferredSearchQuery,
            locale,
            translateFn: translate,
        });

        const matchingIdsSet = new Set(matchedIds);
        const matchedFilters = filtersConfig.filter((filter) => matchingIdsSet.has(filter.key as TKey));

        return {
            matchedFilters,
            autoExpandFilterKey,
        };
    }, [service, filtersConfig, deferredSearchQuery, locale, translate]);

    return {
        isSearchActive,
        searchResults,
    };
};
